/**
 * PortalIntake - Mandatory client information intake form
 * Full-screen form that must be completed before accessing the portal.
 * Reuses questionnaire rendering patterns with section grouping, auto-save,
 * conditional visibility, and all question types.
 * Deployed to: frontend/src/pages/portal/PortalIntake.js
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList, CheckCircle, AlertCircle, Save, Send, Shield, ChevronDown, ChevronUp, Clock, X
} from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';

const PortalIntake = () => {
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  // Save indicator state machine: 'idle' | 'saving' | 'saved' | 'retrying' | 'failed'
  const [saveState, setSaveState] = useState('idle');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [completedSections, setCompletedSections] = useState({});
  const [finishingLater, setFinishingLater] = useState(false);
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [finishError, setFinishError] = useState('');
  const autoSaveTimer = useRef(null);
  const retryTimer = useRef(null);
  const savedRevertTimer = useRef(null);

  useEffect(() => {
    loadIntake();
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      if (retryTimer.current) clearTimeout(retryTimer.current);
      if (savedRevertTimer.current) clearTimeout(savedRevertTimer.current);
    };
  }, []);

  const loadIntake = async () => {
    // TODO(form_version): Questionnaire templates currently mutate in place — there is
    // no version pinning on portal_questionnaires, portal_questionnaire_questions, or
    // portal_questionnaire_assignments. If a template is edited between draft creation
    // and resume, the resumed form may have new/removed/changed questions and the
    // existing responses will simply attach to whatever survives. This is an accepted
    // risk while template churn is low. Revisit (add a `version` column on the template
    // and pin `questionnaire_version` on the assignment) if a template change ever
    // conflicts with an in-flight draft. See spike notes 2026-05-09.
    setLoading(true);
    try {
      const data = await clientPortalService.getIntakeAssignment();
      setQuestionnaire(data);

      // If already completed, redirect to dashboard
      if (data.status === 'completed') {
        const info = clientPortalService.getClientInfo();
        if (info) {
          info.intake_completed = true;
          localStorage.setItem('client_portal_contact', JSON.stringify(info));
        }
        navigate('/portal/dashboard', { replace: true });
        return;
      }

      // Nothing to complete: no intake template/assignment for this tenant.
      // Treat like "done" and proceed to the dashboard.
      if (data.status === 'not_required' || !data.assignment_id || (data.questions || []).length === 0) {
        const info = clientPortalService.getClientInfo();
        if (info) {
          info.intake_completed = true;
          localStorage.setItem('client_portal_contact', JSON.stringify(info));
        }
        navigate('/portal/dashboard', { replace: true });
        return;
      }

      // Populate existing responses
      const existing = {};
      (data.questions || []).forEach(q => {
        if (q.response) {
          existing[q.id] = q.response.response_text || q.response.response_data || '';
        }
      });
      setResponses(existing);

      // Expand first section by default
      const sections = {};
      (data.questions || []).forEach(q => {
        if (q.section && !sections[q.section]) {
          sections[q.section] = true;
        }
      });
      const sectionNames = Object.keys(sections);
      if (sectionNames.length > 0) {
        setExpandedSections({ [sectionNames[0]]: true });
      }
    } catch (err) {
      console.error('Failed to load intake form:', err);
      // Belt-and-suspenders: a missing/failed assignment lookup must not trap the
      // client on an error wall mid-onboarding. Proceed to the dashboard rather
      // than blocking access; intake_completed stays false so a later login can
      // re-route here once the backend is healthy.
      navigate('/portal/dashboard', { replace: true });
      return;
    } finally {
      setLoading(false);
    }
  };

  const updateResponse = (questionId, value) => {
    const newResponses = { ...responses, [questionId]: value };
    setResponses(newResponses);
    // Debounced auto-save. Fresh input invalidates any pending retry of stale data.
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    if (retryTimer.current) { clearTimeout(retryTimer.current); retryTimer.current = null; }
    autoSaveTimer.current = setTimeout(() => autoSave(newResponses), 2000);
  };

  // Retry backoffs in ms: 2s / 5s / 15s. After three failed retries (4 attempts total),
  // surface the persistent failure banner.
  const RETRY_BACKOFFS = [2000, 5000, 15000];

  const autoSave = async (currentResponses, attempt = 0) => {
    if (!questionnaire || questionnaire.status === 'completed') return;
    if (attempt === 0) {
      // Manual or fresh autosave — drop any pending retry of older data.
      if (retryTimer.current) { clearTimeout(retryTimer.current); retryTimer.current = null; }
    }
    if (savedRevertTimer.current) { clearTimeout(savedRevertTimer.current); savedRevertTimer.current = null; }
    setSaveState(attempt === 0 ? 'saving' : 'retrying');

    const respArray = Object.entries(currentResponses).map(([qid, val]) => ({
      question_id: parseInt(qid),
      response_text: typeof val === 'string' ? val : null,
      response_data: typeof val === 'object' ? val : null,
    }));

    try {
      await clientPortalService.saveQuestionnaireResponses(questionnaire.assignment_id, respArray);
      setSaveState('saved');
      // Revert indicator to neutral after ~2s, matching the ✓ Saved → idle pattern.
      savedRevertTimer.current = setTimeout(() => setSaveState('idle'), 2000);
    } catch (err) {
      // Audit-safe logging: status code + endpoint + timestamp only. Never log err itself
      // or err.response.data — those can echo user-typed answers (incl. sensitive fields).
      const status = err?.response?.status ?? 'network';
      // eslint-disable-next-line no-console
      console.error(
        `[autoSave] ${new Date().toISOString()} status=${status} ` +
        `endpoint=PUT /client-portal/questionnaires/${questionnaire.assignment_id}/responses ` +
        `attempt=${attempt}`
      );

      if (attempt < RETRY_BACKOFFS.length) {
        setSaveState('retrying');
        retryTimer.current = setTimeout(
          () => autoSave(currentResponses, attempt + 1),
          RETRY_BACKOFFS[attempt]
        );
      } else {
        setSaveState('failed');
      }
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      // Save first
      await autoSave(responses);
      const result = await clientPortalService.submitQuestionnaire(questionnaire.assignment_id);
      if (result.success) {
        // Update localStorage to reflect intake completion
        const info = clientPortalService.getClientInfo();
        if (info) {
          info.intake_completed = true;
          localStorage.setItem('client_portal_contact', JSON.stringify(info));
        }
        navigate('/portal/dashboard', { replace: true });
      } else {
        setError(result.error || 'Please complete all required fields before submitting.');
        // Scroll to top to show error
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  // Save & Finish Later: flush pending auto-save synchronously, then ask the
  // backend to email a resume link, then surface a confirmation modal. Bypasses
  // the autoSave retry chain — we want immediate pass/fail here. Save failure
  // and email failure surface different errors because the save is load-bearing
  // and the email is a convenience.
  const handleFinishLater = async () => {
    if (!questionnaire) return;
    setFinishError('');
    setFinishingLater(true);
    if (autoSaveTimer.current) { clearTimeout(autoSaveTimer.current); autoSaveTimer.current = null; }
    if (retryTimer.current) { clearTimeout(retryTimer.current); retryTimer.current = null; }
    setSaveState('saving');

    const respArray = Object.entries(responses).map(([qid, val]) => ({
      question_id: parseInt(qid),
      response_text: typeof val === 'string' ? val : null,
      response_data: typeof val === 'object' ? val : null,
    }));

    try {
      await clientPortalService.saveQuestionnaireResponses(questionnaire.assignment_id, respArray);
      setSaveState('saved');
      if (savedRevertTimer.current) clearTimeout(savedRevertTimer.current);
      savedRevertTimer.current = setTimeout(() => setSaveState('idle'), 2000);
    } catch (err) {
      const status = err?.response?.status ?? 'network';
      // eslint-disable-next-line no-console
      console.error(
        `[finishLater.save] ${new Date().toISOString()} status=${status} ` +
        `assignment=${questionnaire.assignment_id}`
      );
      setSaveState('failed');
      setFinishError(
        "We couldn't save your progress just now. Please check your connection and try again — " +
        "your answers are still typed in on this page until you close the tab."
      );
      setFinishingLater(false);
      return;
    }

    try {
      await clientPortalService.sendIntakeResumeLink(questionnaire.assignment_id);
      setShowFinishModal(true);
    } catch (err) {
      const status = err?.response?.status ?? 'network';
      // eslint-disable-next-line no-console
      console.error(
        `[finishLater.email] ${new Date().toISOString()} status=${status} ` +
        `assignment=${questionnaire.assignment_id}`
      );
      // Save succeeded; only the email failed. Still show the modal but acknowledge
      // the email may be delayed — they can return to the portal directly any time.
      setShowFinishModal(true);
      setFinishError(
        "Your progress is saved, but we couldn't send the reminder email. " +
        "You can come back to your portal any time at app.virgolaw.app."
      );
    } finally {
      setFinishingLater(false);
    }
  };

  // Check conditional visibility
  const isVisible = (question) => {
    if (!question.conditional_on) return true;
    const cond = question.conditional_on;
    const parentId = cond.question_id;
    const answer = responses[parentId];

    // Value-based condition (select matches)
    if (cond.value) {
      if (Array.isArray(cond.value)) return cond.value.includes(answer);
      return answer === cond.value;
    }

    // Numeric min_value condition (number > threshold)
    if (cond.min_value !== undefined) {
      const num = parseInt(answer || '0', 10);
      return num >= cond.min_value;
    }

    return true;
  };

  // Check if a section is complete
  const checkSectionComplete = useCallback((questions) => {
    const required = questions.filter(q => q.is_required && isVisible(q));
    return required.every(q => {
      const val = responses[q.id];
      return val && (typeof val === 'string' ? val.trim() !== '' : true);
    });
  }, [responses]);

  // Calculate progress
  const getProgress = () => {
    if (!questionnaire) return { answered: 0, total: 0, percent: 0 };
    const visible = (questionnaire.questions || []).filter(q => isVisible(q));
    const required = visible.filter(q => q.is_required);
    const answered = required.filter(q => {
      const val = responses[q.id];
      return val && (typeof val === 'string' ? val.trim() !== '' : true);
    });
    return {
      answered: answered.length,
      total: required.length,
      percent: required.length > 0 ? Math.round((answered.length / required.length) * 100) : 0,
    };
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const renderQuestion = (q) => {
    if (!isVisible(q)) return null;
    const val = responses[q.id] || '';
    const validation = q.validation_rules || {};

    const inputClass = "w-full px-4 py-2.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all";

    return (
      <div key={q.id} className="space-y-2">
        <label className="block text-sm font-medium text-virgo-teal-300">
          {q.question_text}
          {q.is_required && <span className="text-red-400 ml-1">*</span>}
        </label>
        {q.help_text && <p className="text-xs text-virgo-teal-500">{q.help_text}</p>}

        {q.question_type === 'text' && (
          <input
            type="text"
            value={val}
            onChange={e => updateResponse(q.id, e.target.value)}
            className={inputClass}
            placeholder="Enter your answer"
            maxLength={validation.max_length || undefined}
          />
        )}

        {q.question_type === 'textarea' && (
          <textarea
            value={val}
            onChange={e => updateResponse(q.id, e.target.value)}
            className={inputClass}
            rows={3}
            placeholder="Enter your answer"
          />
        )}

        {q.question_type === 'number' && (
          <input
            type="number"
            value={val}
            onChange={e => updateResponse(q.id, e.target.value)}
            className={inputClass}
            placeholder="0"
            min="0"
          />
        )}

        {q.question_type === 'currency' && (
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-virgo-teal-500">$</span>
            <input
              type="number"
              step="0.01"
              value={val}
              onChange={e => updateResponse(q.id, e.target.value)}
              className={`${inputClass} pl-7`}
              placeholder="0.00"
            />
          </div>
        )}

        {q.question_type === 'date' && (
          <input
            type="date"
            value={val}
            onChange={e => updateResponse(q.id, e.target.value)}
            className={inputClass}
          />
        )}

        {q.question_type === 'select' && (
          <select
            value={val}
            onChange={e => updateResponse(q.id, e.target.value)}
            className={inputClass}
          >
            <option value="">Select an option</option>
            {(q.options || []).map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        )}

        {q.question_type === 'yes_no' && (
          <div className="flex gap-3">
            {['Yes', 'No'].map(opt => (
              <button
                key={opt}
                type="button"
                onClick={() => updateResponse(q.id, opt)}
                className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                  val === opt
                    ? 'bg-cyan-500 text-white'
                    : 'bg-virgo-navy-600 text-virgo-teal-300 border border-virgo-teal-700/50 hover:bg-virgo-navy-500'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-500 mx-auto mb-4" />
          <p className="text-virgo-teal-400">Loading your intake form...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (!questionnaire) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
          <p className="text-red-300 mb-4">{error || 'Unable to load intake form'}</p>
          <button onClick={loadIntake} className="px-6 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Group questions by section
  const sections = {};
  const sectionOrder = [];
  (questionnaire.questions || []).forEach(q => {
    const section = q.section || 'General';
    if (!sections[section]) {
      sections[section] = [];
      sectionOrder.push(section);
    }
    sections[section].push(q);
  });

  const progress = getProgress();

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900">
      {/* Header */}
      <header className="bg-virgo-navy-800/80 border-b border-virgo-teal-700/30 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-cyan-500/20 rounded-lg">
                <ClipboardList className="text-cyan-400" size={22} />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">
                  {questionnaire.title || 'Complete Your Client Information'}
                </h1>
                <p className="text-xs text-virgo-teal-500">
                  {progress.answered}/{progress.total} required fields completed
                  {questionnaire.practice_area && (
                    <span className="ml-2 px-2 py-0.5 bg-cyan-500/20 text-cyan-300 rounded-full text-xs">
                      {questionnaire.practice_area}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {saveState === 'saving' && (
                <span className="text-virgo-teal-500 text-xs flex items-center gap-1">
                  <Save size={12} /> Saving...
                </span>
              )}
              {saveState === 'saved' && (
                <span className="text-emerald-400 text-xs flex items-center gap-1">
                  ✓ Saved
                </span>
              )}
              {saveState === 'retrying' && (
                <span className="text-amber-400 text-xs flex items-center gap-1">
                  ⚠ Save failed — retrying
                </span>
              )}
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-3 w-full bg-virgo-navy-600 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress.percent}%` }}
            />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Confidentiality notice */}
        <div className="bg-virgo-navy-700/50 border border-virgo-teal-700/20 rounded-xl p-5 mb-8">
          <div className="flex items-start gap-3">
            <Shield className="text-cyan-400 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h2 className="text-white font-semibold text-sm mb-1">Confidential & Privileged</h2>
              <p className="text-virgo-teal-400 text-sm leading-relaxed">
                All information you provide is protected by attorney-client privilege and will be kept strictly
                confidential. This information helps us provide the best possible legal representation. Your
                progress is saved automatically as you type.
              </p>
            </div>
          </div>
        </div>

        {/* Persistent save-failed banner — appears after retries are exhausted. */}
        {saveState === 'failed' && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle size={18} className="text-red-400 flex-shrink-0 mt-0.5" />
            <span className="text-red-300 text-sm leading-relaxed">
              We couldn't save your progress. Please check your connection and click{' '}
              <strong>Save &amp; Finish Later</strong> to retry, or copy your answers before closing this tab.
            </span>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle size={18} className="text-red-400 flex-shrink-0" />
            <span className="text-red-300 text-sm">{error}</span>
          </div>
        )}

        {/* Sections */}
        {sectionOrder.map((section, idx) => {
          const questions = sections[section];
          const isExpanded = expandedSections[section] !== false;
          const visibleQuestions = questions.filter(q => isVisible(q));
          const requiredInSection = visibleQuestions.filter(q => q.is_required);
          const answeredInSection = requiredInSection.filter(q => {
            const val = responses[q.id];
            return val && (typeof val === 'string' ? val.trim() !== '' : true);
          });
          const sectionComplete = requiredInSection.length > 0 && answeredInSection.length === requiredInSection.length;

          return (
            <div key={section} className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 mb-4 overflow-hidden">
              {/* Section header — clickable accordion */}
              <button
                type="button"
                onClick={() => toggleSection(section)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-virgo-navy-600/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs text-virgo-teal-500 bg-virgo-navy-600 rounded-full w-6 h-6 flex items-center justify-center font-mono">
                    {idx + 1}
                  </span>
                  <h2 className="text-base font-semibold text-white">{section}</h2>
                  {sectionComplete && (
                    <CheckCircle size={16} className="text-emerald-400" />
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {requiredInSection.length > 0 && (
                    <span className="text-xs text-virgo-teal-500">
                      {answeredInSection.length}/{requiredInSection.length}
                    </span>
                  )}
                  {isExpanded ? <ChevronUp size={18} className="text-virgo-teal-500" /> : <ChevronDown size={18} className="text-virgo-teal-500" />}
                </div>
              </button>

              {/* Section content */}
              {isExpanded && (
                <div className="px-6 pb-6 space-y-5 border-t border-virgo-teal-700/20 pt-4">
                  {questions.map(q => renderQuestion(q))}
                </div>
              )}
            </div>
          );
        })}

        {/* Submit area */}
        <div className="mt-8 mb-12">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <button
              type="button"
              onClick={() => autoSave(responses)}
              disabled={saveState === 'saving' || saveState === 'retrying'}
              className="px-6 py-3 bg-virgo-navy-600 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-500 disabled:opacity-50 flex items-center gap-2 transition-all text-sm"
            >
              <Save size={16} />{' '}
              {saveState === 'saving'
                ? 'Saving...'
                : saveState === 'retrying'
                ? 'Retrying…'
                : 'Save Progress'}
            </button>
            <button
              type="button"
              onClick={handleFinishLater}
              disabled={finishingLater || submitting}
              className="px-6 py-3 bg-virgo-navy-600 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-500 disabled:opacity-50 flex items-center gap-2 transition-all text-sm"
            >
              <Clock size={16} />{' '}
              {finishingLater ? 'Saving…' : 'Save & Finish Later'}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || saveState === 'failed'}
              className="px-10 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all shadow-lg shadow-cyan-500/30"
            >
              <Send size={18} /> {submitting ? 'Submitting...' : 'Submit & Continue to Portal'}
            </button>
          </div>
          {progress.percent < 100 && (
            <p className="text-center text-virgo-teal-500 text-xs mt-3">
              Complete all required fields ({progress.total - progress.answered} remaining) to submit
            </p>
          )}
          {finishError && (
            <p className="text-center text-amber-300 text-xs mt-3">{finishError}</p>
          )}
        </div>
      </main>

      {/* Save & Finish Later confirmation modal */}
      {showFinishModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setShowFinishModal(false)}
        >
          <div
            className="bg-virgo-navy-700 border border-virgo-teal-700/40 rounded-2xl shadow-2xl max-w-md w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowFinishModal(false)}
              className="absolute top-3 right-3 text-virgo-teal-500 hover:text-white"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-emerald-500/20 rounded-lg flex-shrink-0">
                <CheckCircle className="text-emerald-400" size={22} />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg mb-1">Your progress is saved</h3>
                <p className="text-virgo-teal-300 text-sm leading-relaxed">
                  We'll remind you at your next login. We've also emailed you a link to come back directly.
                </p>
              </div>
            </div>
            {finishError && (
              <p className="text-amber-300 text-xs mb-3">{finishError}</p>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => navigate('/portal/dashboard')}
                className="px-5 py-2 bg-cyan-500 text-white text-sm font-medium rounded-lg hover:bg-cyan-600"
              >
                Go to Portal
              </button>
              <button
                type="button"
                onClick={() => setShowFinishModal(false)}
                className="px-5 py-2 bg-virgo-navy-600 text-virgo-teal-300 text-sm rounded-lg hover:bg-virgo-navy-500"
              >
                Keep Editing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalIntake;
